// A1 — Configuración del Tenant (datos generales + configuración operativa).
//
// ⚠ `timezone`, `atribucion_turno_cruce` y `dia_inicio_semana` NO son datos
// decorativos: de ellos se DERIVAN el día operativo y la semana operativa. Al
// cambiarlos, los períodos YA CERRADOS se vuelven a agrupar con la nueva
// regla, así que los informes históricos pueden mostrar cifras distintas. El
// cambio NO reescribe ningún registro histórico — la producción capturada
// queda intacta; lo único que cambia es cómo las consultas futuras la
// agrupan. Por eso la pantalla avisa y pide confirmación antes de guardar.

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { useAdminTenant, useUpdateAdminTenant } from '../../lib/api/queries';
import { evaluarFormatoLote } from './formatoLoteAdmin';

/** Días ISO de la semana operativa (1 = lunes … 7 = domingo). */
const DIAS_ISO = [
  { valor: '1', label: 'Lunes' },
  { valor: '2', label: 'Martes' },
  { valor: '3', label: 'Miércoles' },
  { valor: '4', label: 'Jueves' },
  { valor: '5', label: 'Viernes' },
  { valor: '6', label: 'Sábado' },
  { valor: '7', label: 'Domingo' },
] as const;

const AVISO_PERIODOS_PASADOS =
  'Advertencia: estos tres parámetros definen cómo se calculan el día operativo y la ' +
  'semana operativa. Si usted los cambia, los períodos YA CERRADOS se vuelven a agrupar ' +
  'con la nueva regla, de modo que informes, cierres y comparativos históricos pueden ' +
  'mostrar cifras distintas a las que usted vio antes. Ningún registro histórico se ' +
  'reescribe: la producción capturada queda intacta y solo cambia la forma en que las ' +
  'consultas futuras la agrupan.';

export function A1ConfigTenantPage() {
  const { data, isLoading, isError } = useAdminTenant();
  const update = useUpdateAdminTenant();
  const [form, setForm] = useState({
    nombre: '',
    nit: '',
    logo_url: '',
    contacto_email: '',
    meta_facturacion: '',
    timezone: '',
    atribucion_turno_cruce: 'dia_inicio' as 'dia_inicio' | 'dia_fin',
    dia_inicio_semana: '1',
    formato_lote_regex: '',
    formato_lote_ejemplo: '',
    formato_lote_ayuda: '',
  });

  useEffect(() => {
    if (data) {
      setForm({
        nombre: data.nombre,
        nit: data.nit,
        logo_url: data.logo_url ?? '',
        contacto_email: data.contacto_email,
        meta_facturacion:
          data.meta_facturacion_mensual_millones === null
            ? ''
            : String(data.meta_facturacion_mensual_millones),
        timezone: data.timezone,
        atribucion_turno_cruce: data.atribucion_turno_cruce,
        dia_inicio_semana: String(data.dia_inicio_semana),
        formato_lote_regex: data.formato_lote_regex,
        formato_lote_ejemplo: data.formato_lote_ejemplo,
        formato_lote_ayuda: data.formato_lote_ayuda,
      });
    }
  }, [data]);

  // ¿Tocó algo que cambia el cálculo de períodos? Se compara contra lo que
  // está guardado, no contra el default: solo se avisa si hay cambio real.
  const cambiaCalculoPeriodos =
    data !== undefined &&
    (form.timezone.trim() !== data.timezone ||
      form.atribucion_turno_cruce !== data.atribucion_turno_cruce ||
      Number(form.dia_inicio_semana) !== data.dia_inicio_semana);

  // Formato del lote (B7): validación en vivo (expresión compila + el ejemplo
  // la cumple). El API repite la misma validación al guardar.
  const formatoLote = evaluarFormatoLote(form.formato_lote_regex, form.formato_lote_ejemplo);

  function guardar() {
    // Meta opcional: vacío = quitar la meta (null). Debe ser > 0.
    const metaTexto = form.meta_facturacion.trim();
    const meta = metaTexto === '' ? null : Number(metaTexto);
    if (meta !== null && (!Number.isFinite(meta) || meta <= 0)) {
      toast.error('La meta de facturación debe ser un número mayor que 0 (en millones COP).');
      return;
    }
    const timezone = form.timezone.trim();
    if (timezone === '') {
      toast.error('Indique la zona horaria del tenant (nombre IANA, ej. America/Bogota).');
      return;
    }
    const diaInicioSemana = Number(form.dia_inicio_semana);
    if (!Number.isInteger(diaInicioSemana) || diaInicioSemana < 1 || diaInicioSemana > 7) {
      toast.error('El día de inicio de semana debe estar entre 1 (lunes) y 7 (domingo).');
      return;
    }
    if (formatoLote.error !== null) {
      toast.error(formatoLote.error);
      return;
    }
    if (form.formato_lote_ayuda.trim() === '') {
      toast.error('Indique la ayuda del formato de lote (ej. 8 dígitos).');
      return;
    }
    if (
      cambiaCalculoPeriodos &&
      !confirm(
        `${AVISO_PERIODOS_PASADOS}\n\n¿Confirma que desea guardar el cambio de configuración operativa?`,
      )
    ) {
      return;
    }
    update.mutate(
      {
        nombre: form.nombre,
        nit: form.nit,
        logo_url: form.logo_url.trim() === '' ? null : form.logo_url.trim(),
        contacto_email: form.contacto_email,
        meta_facturacion_mensual_millones: meta,
        timezone,
        atribucion_turno_cruce: form.atribucion_turno_cruce,
        dia_inicio_semana: diaInicioSemana,
        formato_lote_regex: form.formato_lote_regex.trim(),
        formato_lote_ejemplo: form.formato_lote_ejemplo.trim(),
        formato_lote_ayuda: form.formato_lote_ayuda.trim(),
      },
      {
        onSuccess: () => toast.success('Datos del tenant actualizados'),
        onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error al guardar'),
      },
    );
  }

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <header>
          <h1 className="text-xl font-medium text-fg">Configuración del Tenant</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">Datos generales · solo Admin</p>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col gap-4 p-5">
              <Field
                label="Nombre"
                value={form.nombre}
                onChange={(v) => setForm({ ...form, nombre: v })}
              />
              <Field label="NIT" value={form.nit} onChange={(v) => setForm({ ...form, nit: v })} />
              <Field
                label="Logo URL"
                value={form.logo_url}
                onChange={(v) => setForm({ ...form, logo_url: v })}
                placeholder="https://…"
              />
              <Field
                label="Email de contacto"
                value={form.contacto_email}
                onChange={(v) => setForm({ ...form, contacto_email: v })}
                type="email"
              />
              <div className="flex flex-col gap-1">
                <Field
                  label="Meta de facturación mensual (millones COP)"
                  value={form.meta_facturacion}
                  onChange={(v) => setForm({ ...form, meta_facturacion: v })}
                  type="number"
                  placeholder="ej. 850 = $850.000.000 · vacío = sin meta"
                />
                {form.meta_facturacion.trim() !== '' &&
                  Number.isFinite(Number(form.meta_facturacion)) &&
                  Number(form.meta_facturacion) > 0 && (
                    <p className="text-[11px] tabular-nums text-fg-tertiary">
                      = ${(Number(form.meta_facturacion) * 1_000_000).toLocaleString('es-CO')} COP
                      al mes · la usa el Informe Gerencial del coordinador
                    </p>
                  )}
              </div>
              <div className="flex flex-col gap-4 border-t border-border pt-4">
                <div>
                  <h2 className="text-sm font-medium text-fg">Configuración operativa</h2>
                  <p className="mt-1 text-[11px] text-fg-tertiary">
                    Define cómo se arma el día operativo y la semana operativa de todo el tenant.
                  </p>
                </div>
                <div
                  role="note"
                  className="rounded border border-warn/40 bg-warn/10 p-3 text-[11px] leading-relaxed text-fg"
                >
                  <strong className="block">Léalo antes de guardar</strong>
                  {AVISO_PERIODOS_PASADOS}
                </div>

                <div className="flex flex-col gap-1">
                  <Field
                    label="Zona horaria (IANA)"
                    value={form.timezone}
                    onChange={(v) => setForm({ ...form, timezone: v })}
                    placeholder="America/Bogota"
                  />
                  <p className="text-[11px] text-fg-tertiary">
                    Hora local con la que se fecha cada captura. Determina en qué día operativo cae
                    una hora y, por lo tanto, el corte de todos los informes diarios.
                  </p>
                </div>

                <div className="flex flex-col gap-1">
                  <Label>Turnos que cruzan medianoche</Label>
                  <Select
                    value={form.atribucion_turno_cruce}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        atribucion_turno_cruce: e.target.value as 'dia_inicio' | 'dia_fin',
                      })
                    }
                  >
                    <option value="dia_inicio">Se atribuyen al día en que INICIA el turno</option>
                    <option value="dia_fin">Se atribuyen al día en que TERMINA el turno</option>
                  </Select>
                  <p className="text-[11px] text-fg-tertiary">
                    Un turno T3 que va de 22:00 a 06:00 produce en dos fechas de calendario. Esta
                    regla decide a cuál de las dos se le suma toda esa producción.
                  </p>
                </div>

                <div className="flex flex-col gap-1">
                  <Label>Día de inicio de la semana operativa</Label>
                  <Select
                    value={form.dia_inicio_semana}
                    onChange={(e) => setForm({ ...form, dia_inicio_semana: e.target.value })}
                  >
                    {DIAS_ISO.map((d) => (
                      <option key={d.valor} value={d.valor}>
                        {d.valor} · {d.label}
                      </option>
                    ))}
                  </Select>
                  <p className="text-[11px] text-fg-tertiary">
                    Primer día de la semana operativa (ISO: 1 = lunes … 7 = domingo). Mueve el corte
                    de los resúmenes y comparativos semanales.
                  </p>
                </div>

                {cambiaCalculoPeriodos && (
                  <p className="text-[11px] font-medium text-warn">
                    Hay cambios de configuración operativa sin guardar. Se le pedirá confirmación:
                    los períodos ya cerrados se recalcularán con la nueva regla (sin reescribir
                    ningún registro).
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-4 border-t border-border pt-4">
                <div>
                  <h2 className="text-sm font-medium text-fg">Formato del lote de captura</h2>
                  <p className="mt-1 text-[11px] text-fg-tertiary">
                    Regla que debe cumplir el campo «OP / Lote» al capturar, editar, corregir o
                    completar una hora, y el lote que reporta la máquina. Por defecto, 8 dígitos.
                    Los lotes ya guardados no se revalidan.
                  </p>
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="formato-lote-regex">Expresión regular</Label>
                  <Input
                    id="formato-lote-regex"
                    className="font-mono"
                    value={form.formato_lote_regex}
                    maxLength={200}
                    placeholder="^[0-9]{8}$"
                    aria-invalid={formatoLote.errorRegex !== null || undefined}
                    onChange={(e) => setForm({ ...form, formato_lote_regex: e.target.value })}
                  />
                  {formatoLote.errorRegex ? (
                    <p className="text-[11px] text-crit">{formatoLote.errorRegex}</p>
                  ) : (
                    <p className="text-[11px] text-fg-tertiary">
                      Sintaxis de JavaScript, sin banderas. Use ^ y $ para exigir el valor completo;
                      ej. ^L[0-9]{'{6}'}-[0-9]{'{3}'}$ para L261012-001.
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="formato-lote-ejemplo">Ejemplo de lote válido</Label>
                  <Input
                    id="formato-lote-ejemplo"
                    className="font-mono"
                    value={form.formato_lote_ejemplo}
                    maxLength={32}
                    aria-invalid={formatoLote.errorEjemplo !== null || undefined}
                    onChange={(e) => setForm({ ...form, formato_lote_ejemplo: e.target.value })}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="formato-lote-ayuda">Ayuda para el usuario</Label>
                  <Input
                    id="formato-lote-ayuda"
                    value={form.formato_lote_ayuda}
                    maxLength={80}
                    placeholder="8 dígitos"
                    onChange={(e) => setForm({ ...form, formato_lote_ayuda: e.target.value })}
                  />
                </div>

                <div
                  role="status"
                  aria-label="Vista previa del formato de lote"
                  className={
                    formatoLote.error === null
                      ? 'rounded border border-ok/30 bg-ok-soft p-3 text-[11px] text-fg'
                      : 'rounded border border-crit/30 bg-crit-soft p-3 text-[11px] text-fg'
                  }
                >
                  <p>
                    Así lo verá quien captura:{' '}
                    <span className="font-mono">
                      Ej. {form.formato_lote_ejemplo.trim() || '…'} (
                      {form.formato_lote_ayuda.trim() || '…'})
                    </span>
                  </p>
                  <p className="mt-1 font-medium">
                    {formatoLote.error === null
                      ? 'El ejemplo cumple el formato.'
                      : formatoLote.error}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-border pt-4">
                <span className="text-[11px] text-fg-tertiary">Plan: {data.plan}</span>
                <Button onClick={guardar} disabled={update.isPending}>
                  {update.isPending ? 'Guardando…' : 'Guardar cambios'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </RoleLayout>
  );
}

function Field({
  label,
  value,
  onChange,
  type,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
